import { Check, ChevronsUpDown } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/utils/cn';

import type { TransformerSummary } from '../types/transformer.types';
import { connectorLabel, formatLabel } from '../utils/transformer-display';

interface Props {
  transformers: readonly TransformerSummary[];
  value: string | null;
  onChange: (id: string) => void;
  placeholder: string;
  /** id on the trigger, so an external `<Label htmlFor>` can name it. */
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  /** What follows the name: the writer format (default) or the connector. */
  meta?: 'format' | 'connector';
  /** E2E `data-testid` for the trigger; popover/items derive `${testId}-...`. */
  testId?: string;
}

/**
 * Searchable transformer picker (Popover + Command, like `Combobox`): typing filters
 * on name and customer instead of a native select's first-letter jump. Modal popover
 * so its list scrolls inside a Dialog / Sheet.
 */
export function TransformerPicker({
  transformers,
  value,
  onChange,
  placeholder,
  id,
  ariaLabel,
  disabled,
  className,
  meta = 'format',
  testId = 'transformer-picker',
}: Props) {
  const { t } = useTranslation('transformers');
  const listId = useId();
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const selected = transformers.find((tr) => tr.id === value);
  const metaOf = (tr: TransformerSummary) =>
    meta === 'connector' ? connectorLabel(tr.connector) : formatLabel(tr.format);

  return (
    <Popover modal open={open} {...(!disabled && { onOpenChange: setOpen })}>
      {/* div[role="combobox"] avoids nesting <button> inside a <label> / <button>. */}
      <PopoverTrigger asChild>
        <div
          data-testid={testId}
          id={id}
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) setOpen(true);
          }}
          className={cn(
            'border-input bg-background data-[state=open]:ring-ring flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm focus:outline-none data-[state=open]:ring-2',
            disabled && 'pointer-events-none cursor-not-allowed opacity-50',
            className,
          )}
        >
          {selected ? (
            <span className="truncate" title={selected.name}>
              {selected.name} · {metaOf(selected)}
            </span>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder}</span>
          )}
          <ChevronsUpDown aria-hidden="true" className="text-muted-foreground h-4 w-4 shrink-0" />
        </div>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`${testId}-popover`}
        className="flex max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) min-w-72 flex-col overflow-hidden p-0"
        align="start"
        onOpenAutoFocus={(event) => {
          // Focus the search box (not the container) so typing filters straight away.
          event.preventDefault();
          searchRef.current?.focus();
        }}
      >
        {/* Match on name + customer only: the item value is the id, which must not match. */}
        <Command
          className="min-h-0 flex-1"
          filter={(_value, search, keywords) =>
            (keywords ?? []).join(' ').toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput
            data-testid={`${testId}-search-input`}
            ref={searchRef}
            placeholder={t('picker.search')}
          />
          <CommandList id={listId} className="min-h-0 flex-1">
            <CommandEmpty>{t('picker.empty')}</CommandEmpty>
            <CommandGroup>
              {transformers.map((tr) => (
                <CommandItem
                  key={tr.id}
                  data-testid={`${testId}-item-${tr.id}`}
                  value={tr.id}
                  keywords={[tr.name, tr.customer ?? '']}
                  onSelect={() => {
                    onChange(tr.id);
                    setOpen(false);
                  }}
                  className="cursor-pointer items-start"
                >
                  <Check
                    aria-hidden="true"
                    className={cn(
                      'mt-0.5 mr-2 h-4 w-4 shrink-0',
                      tr.id === value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate" title={tr.name}>
                      {tr.name}
                    </span>
                    <span className="text-muted-foreground truncate text-xs">
                      {[tr.customer, metaOf(tr)].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
